import { TIER_COLORS, COACHING_STYLES, THEME_ACCENTS, THEME_BACKGROUNDS, TIER_PROGRESSION } from "@/types/evolve";

const BRAND_COLORS = [
  { name: "Primary (Evolve Red)", hsl: "355 82% 56%", hex: "#E63946", cssVar: "--primary" },
  { name: "Background", hsl: "0 0% 4%", hex: "#0A0A0A", cssVar: "--background" },
  { name: "Card", hsl: "0 0% 6%", hex: "#0F0F0F", cssVar: "--card" },
  { name: "Secondary", hsl: "0 0% 12%", hex: "#1F1F1F", cssVar: "--secondary" },
  { name: "Muted", hsl: "0 0% 15%", hex: "#262626", cssVar: "--muted" },
  { name: "Border", hsl: "0 0% 18%", hex: "#2E2E2E", cssVar: "--border" },
  { name: "Destructive", hsl: "0 84% 60%", hex: "#EF4444", cssVar: "--destructive" },
];

export default function BrandGuide() {
  return (
    <div className="min-h-screen bg-background text-foreground p-6 md:p-12 max-w-5xl mx-auto space-y-12">
      {/* Header */}
      <header className="space-y-2">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          EVOLVE AI COACH
        </h1>
        <p className="text-muted-foreground text-lg">Brand Guide & Assets</p>
        <p className="text-sm text-muted-foreground">Transform Your Body & Mind — AI-powered fitness coaching with cult-like progression tiers.</p>
      </header>

      {/* Colors */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Colors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {BRAND_COLORS.map((c) => (
            <div key={c.cssVar} className="border border-border rounded-lg overflow-hidden">
              <div className="h-20" style={{ backgroundColor: c.hex }} />
              <div className="p-3 space-y-1">
                <p className="font-medium text-sm">{c.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{c.hex}</p>
                <p className="text-xs text-muted-foreground font-mono">HSL: {c.hsl}</p>
                <p className="text-xs text-muted-foreground font-mono">var({c.cssVar})</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Theme Accents */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Accent Palette</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {THEME_ACCENTS.map((a) => (
            <div key={a.value} className="border border-border rounded-lg overflow-hidden">
              <div className="h-16" style={{ backgroundColor: `hsl(${a.hsl})` }} />
              <div className="p-3">
                <p className="font-medium text-sm">{a.label}</p>
                <p className="text-xs text-muted-foreground font-mono">HSL: {a.hsl}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Theme Backgrounds */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Background Options</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {THEME_BACKGROUNDS.map((b) => (
            <div key={b.value} className="border border-border rounded-lg overflow-hidden">
              <div className="h-16" style={{ backgroundColor: `hsl(${b.hsl})` }} />
              <div className="p-3">
                <p className="font-medium text-sm">{b.label}</p>
                <p className="text-xs text-muted-foreground font-mono">HSL: {b.hsl}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Typography */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Typography</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-border rounded-lg p-6 space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-widest">Display / Headings</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Orbitron</p>
            <p className="text-sm text-muted-foreground font-mono">font-family: 'Orbitron', sans-serif</p>
            <p className="text-sm text-muted-foreground">Weights: 400–900</p>
            <a href="https://fonts.google.com/specimen/Orbitron" target="_blank" rel="noopener" className="text-xs text-primary underline">Google Fonts →</a>
          </div>
          <div className="border border-border rounded-lg p-6 space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-widest">Body</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Inter</p>
            <p className="text-sm text-muted-foreground font-mono">font-family: 'Inter', sans-serif</p>
            <p className="text-sm text-muted-foreground">Weights: 300–700</p>
            <a href="https://fonts.google.com/specimen/Inter" target="_blank" rel="noopener" className="text-xs text-primary underline">Google Fonts →</a>
          </div>
        </div>
      </section>

      {/* Tier System */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Tier Progression System</h2>
        <p className="text-sm text-muted-foreground">16 tiers from Seed to Eternal, unlocked by total workouts completed.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {TIER_PROGRESSION.map(({ tier, workoutsRequired }) => (
            <div key={tier} className="border border-border rounded-lg p-3 space-y-2">
              <div className={`h-8 rounded bg-gradient-to-r ${TIER_COLORS[tier]}`} />
              <p className="font-bold text-sm" style={{ fontFamily: 'Orbitron, sans-serif' }}>{tier}</p>
              <p className="text-xs text-muted-foreground">{workoutsRequired} workouts</p>
            </div>
          ))}
        </div>
      </section>

      {/* Coaching Styles */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Coaching Styles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {COACHING_STYLES.map((s) => (
            <div key={s.value} className="border border-border rounded-lg p-4 space-y-1">
              <p className="font-bold text-sm">{s.emoji} {s.label}</p>
              <p className="text-xs text-muted-foreground">{s.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Effects */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Effects & Components</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-border rounded-lg p-6 evolve-glow text-center">
            <p className="text-sm font-medium">evolve-glow</p>
            <p className="text-xs text-muted-foreground mt-1">Standard glow shadow</p>
          </div>
          <div className="border border-border rounded-lg p-6 evolve-glow-intense text-center">
            <p className="text-sm font-medium">evolve-glow-intense</p>
            <p className="text-xs text-muted-foreground mt-1">Intense glow shadow</p>
          </div>
          <div className="rounded-lg p-6 evolve-gradient text-center">
            <p className="text-sm font-medium">evolve-gradient</p>
            <p className="text-xs text-muted-foreground mt-1">Primary gradient fill</p>
          </div>
          <div className="border border-border rounded-lg p-6 pulse-glow text-center">
            <p className="text-sm font-medium">pulse-glow</p>
            <p className="text-xs text-muted-foreground mt-1">Animated pulsing glow</p>
          </div>
          <div className="border border-border rounded-lg p-6 glass-card text-center">
            <p className="text-sm font-medium">glass-card</p>
            <p className="text-xs text-muted-foreground mt-1">Frosted glass card</p>
          </div>
          <div className="border border-border rounded-lg p-6 text-center">
            <p className="text-sm font-medium evolve-text-gradient">evolve-text-gradient</p>
            <p className="text-xs text-muted-foreground mt-1">Gradient text effect</p>
          </div>
        </div>
      </section>

      {/* Links */}
      <section className="space-y-4">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>Links</h2>
        <div className="border border-border rounded-lg p-4 space-y-2 text-sm">
          <p><span className="text-muted-foreground">Live App:</span> <a href="https://evolve-ai-coach-app.lovable.app" className="text-primary underline" target="_blank" rel="noopener">evolve-ai-coach-app.lovable.app</a></p>
          <p><span className="text-muted-foreground">Privacy Policy:</span> <a href="https://evolveaicoach.app/privacy" className="text-primary underline" target="_blank" rel="noopener">evolveaicoach.app/privacy</a></p>
        </div>
      </section>

      <footer className="text-xs text-muted-foreground pt-8 border-t border-border">
        © Evolve AI Coach. All rights reserved.
      </footer>
    </div>
  );
}
